import { Button } from "@/components/ui/button";
import { createFileRoute, Link } from "@tanstack/react-router";

import PalmanLogo from "../assets/palman_logo.svg?react";
import { Thinker } from "@/components/ui/thinker";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { Trans, useTranslation } from "react-i18next";

import DiscordLogo from "../assets/discord_logo.svg?react";
import { DISCORD_URL, GUIDE_NOTION_URL } from "@/constants/urls";
import {
  GlobeLock,
  Handshake,
  Shield,
  SquareArrowOutUpRightIcon,
} from "lucide-react";
import { useEffect, useRef } from "react";

export const Route = createFileRoute("/")({
  component: RouterComponent,
});

function RouterComponent() {
  const mainVideoRef = useRef<HTMLVideoElement>(null);
  const bulkEditVideoRef = useRef<HTMLVideoElement>(null);
  const slackVideoRef = useRef<HTMLVideoElement>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const observerOptions = {
      threshold: 0, // 화면에 보이는 순간 재생
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement;
        if (entry.isIntersecting) {
          video.play();
        } else {
          video.pause();
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    if (mainVideoRef.current) {
      observer.observe(mainVideoRef.current);
    }
    if (bulkEditVideoRef.current) {
      observer.observe(bulkEditVideoRef.current);
    }
    if (slackVideoRef.current) {
      observer.observe(slackVideoRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div>
      <div className="sticky top-0 bg-background z-50">
        <div className="w-full max-w-4xl mx-auto px-5 py-3">
          <div className="flex flex-row">
            <div className="flex flex-row gap-x-1 items-center">
              <PalmanLogo className="size-6" />
              <span className="text-xl font-semibold">Palman</span>
            </div>
            <div className="flex-1"></div>
            <div className="flex gap-x-2">
              <Link to="/login">
                <Button variant="ghost">
                  <Trans i18nKey={"landing.login"}>Log in</Trans>
                </Button>
              </Link>
              <Link to="/signup">
                <Button>
                  <Trans i18nKey={"landing.signup"}>Sign up</Trans>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <section className="pt-20">
        <div className="w-full max-w-4xl mx-auto px-5">
          <div className="flex items-center justify-center">
            <Thinker className="size-20" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold whitespace-pre-wrap text-center mt-5 break-keep">
            <Trans i18nKey={"landing.title"}>
              Palman is collaborative knowledge management system powered by AI
            </Trans>
          </h1>
          <p className="text-lg text-center mt-3">
            <Trans i18nKey={"landing.description"}>
              Record shared knowledge, easily modify, access through chat
            </Trans>
          </p>
          <div className="flex items-center justify-center mt-6 gap-2">
            <Link to="/login">
              <Button>
                <Trans i18nKey={"landing.getPalman"}>Get Palman</Trans>
              </Button>
            </Link>
            <a href={DISCORD_URL} target="_blank">
              <Button variant={"outline"}>
                <DiscordLogo />
                <Trans i18nKey={"landing.helpCenter"}>Help center</Trans>
              </Button>
            </a>
            <a href={GUIDE_NOTION_URL} target="_blank">
              <Button variant={"outline"}>
                <SquareArrowOutUpRightIcon />
                <Trans i18nKey={"landing.guide"}>Guide</Trans>
              </Button>
            </a>
          </div>
        </div>
      </section>
      <section className="pt-12 pb-20">
        <div className="w-full max-w-7xl mx-auto px-5">
          <div className="flex justify-center">
            <video
              ref={mainVideoRef}
              src="https://api.palman.ai/storage/v1/object/public/assets/landing/main.mov"
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-w-7xl rounded-lg shadow-lg border"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="w-full max-w-7xl mx-auto px-5">
          <h2 className="text-2xl md:text-4xl font-bold">
            <Trans i18nKey={"landing.documentImportOrCreate.title"}>
              Unify and Search Your Knowledge Freely
            </Trans>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3 mt-5">
            <div className="rounded-md border overflow-hidden">
              <div className="p-5">
                <h3 className="text-sm">
                  <Trans
                    i18nKey={"landing.documentImportOrCreate.import.title"}
                  >
                    Import document
                  </Trans>
                </h3>
                <p className="text-xl font-bold mt-1">
                  <Trans
                    i18nKey={
                      "landing.documentImportOrCreate.import.description"
                    }
                  >
                    Bring from external sources
                  </Trans>
                </p>
              </div>
              <img
                src="https://api.palman.ai/storage/v1/object/public/assets/hero/import_source_logos.png"
                alt="import source logos. youtube, slack, discord, notion, excel, word, powerpoint"
                className="border-t"
              />
            </div>
            <div className="rounded-md border overflow-hidden">
              <div className="p-5">
                <h3 className="text-sm">
                  <Trans
                    i18nKey={"landing.documentImportOrCreate.create.title"}
                  >
                    Create document
                  </Trans>
                </h3>
                <p className="text-xl font-bold mt-1">
                  <Trans
                    i18nKey={
                      "landing.documentImportOrCreate.create.description"
                    }
                  >
                    Write down your own knowledge
                  </Trans>
                </p>
              </div>
              <img
                src="https://api.palman.ai/storage/v1/object/public/assets/hero/write_dialog.png"
                alt="document create dialog in palman service"
                className="border-t"
              />
            </div>
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="w-full max-w-7xl mx-auto px-5 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
          <div>
            <h2 className="text-2xl md:text-4xl font-bold">
              <Trans i18nKey={"landing.featuresTitle2"}>
                Integrate naturally into your workflow
              </Trans>
            </h2>
            <p className="text-md text-gray-600 mt-2">
              <Trans i18nKey={"landing.featuresDescription2"}>
                Seamlessly connects with your existing work tools, ready to use
                without any learning curve.
              </Trans>
            </p>
          </div>
          <div className="flex items-center justify-center">
            <video
              ref={slackVideoRef}
              src="https://api.palman.ai/storage/v1/object/public/assets/landing/slack.mov"
              muted
              loop
              playsInline
              className="w-full max-w-5xl rounded-lg shadow-lg border"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>
      <section className="mb-10">
        <div className="w-full max-w-7xl mx-auto px-5 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
          <div>
            <h2 className="text-2xl md:text-4xl font-bold">
              <Trans i18nKey={"landing.featuresTitle3"}>
                AI find and update changed knowledge for you
              </Trans>
            </h2>
            <p className="text-md text-gray-600 mt-2">
              <Trans i18nKey={"landing.featuresDescription3"}>
                Tell us about the changes, and AI will find and update the
                related content for you
              </Trans>
            </p>
          </div>
          <div className="flex justify-center">
            <video
              ref={bulkEditVideoRef}
              src="https://api.palman.ai/storage/v1/object/public/assets/landing/bulk_edit.mov"
              muted
              loop
              playsInline
              className="w-full max-w-7xl rounded-lg shadow-lg border"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="w-full max-w-7xl mx-auto px-5">
          <h2 className="text-2xl md:text-4xl font-bold mb-5 md:mb-10">
            <Trans i18nKey={"landing.featuresTitle4"}>
              Of course, everything comes with robust security
            </Trans>
          </h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              {
                icon: Handshake,
                title: t("landing.security.title1", {
                  defaultValue: "No training on your data",
                }),
                description: t("landing.security.description1", {
                  defaultValue:
                    "We never use your data to train AI models—your information remains private and is never repurposed.",
                }),
              },
              {
                icon: GlobeLock,
                title: t("landing.security.title2", {
                  defaultValue: "End-to-End encryption",
                }),
                description: t("landing.security.description2", {
                  defaultValue:
                    "Your data is secured with AES-256 encryption and RSA-4096 key exchange, ensuring only you and authorized recipients can access your content",
                }),
              },
              {
                icon: Shield,
                title: t("landing.security.title3", {
                  defaultValue: "Precise permission control",
                }),
                description: t("landing.security.description3", {
                  defaultValue:
                    "Control exactly who can view, edit, or share each piece of content with granular permissions you can adjust anytime.",
                }),
              },
            ].map(({ icon: Icon, title, description }) => (
              <div key={title} className="rounded-md bg-accent p-5">
                <Icon />
                <p className="font-bold mt-5">{title}</p>
                <p className="mt-3">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="w-full max-w-7xl mx-auto px-5">
          <h2 className="text-4xl md:text-6xl font-bold">
            <Trans i18nKey={"landing.nudge.title"}>Start for free now</Trans>
          </h2>
          <p className="mt-3">
            <Trans i18nKey="landing.nudge.description">
              Collaborative knowledge management system powered by AI. Record
              shared knowledge, easily modify, access through chat.
            </Trans>
          </p>
          <Link to="/login">
            <Button className="mt-5">
              <Trans i18nKey={"landing.getPalman"}>Get Palman</Trans>
            </Button>
          </Link>
        </div>
      </section>
      <footer className="border-t bg-gray-50/80">
        <div className="w-full max-w-4xl mx-auto px-5 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex flex-row gap-x-1 items-center mb-4">
                <PalmanLogo className="size-6" />
                <span className="text-xl font-semibold">Palman</span>
              </div>
              <LanguageSwitcher />
              <p className="text-gray-600 text-sm max-w-md mt-4 whitespace-pre-wrap">
                <Trans i18nKey={"landing.footerDescription"}>
                  {
                    "Collaborative knowledge management system powered by AI.\nRecord shared knowledge, easily modify, access through chat."
                  }
                </Trans>
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              © 2025 Palman. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
